/**
 * UpgradePrompt - Inline upgrade prompt for premium features
 *
 * A smaller, inline component to prompt upgrades within the UI flow.
 * Uses admin-configured pricing from platform settings.
 */

import { useState } from 'react';
import { Lock, Sparkles, Zap, Loader2, Crown, ArrowRight } from 'lucide-react';
import { type PremiumFeature, FEATURE_INFO, useSubscription } from '../../hooks/useSubscription';
interface UpgradePromptProps {
  feature: PremiumFeature;
  variant?: 'inline' | 'banner' | 'button';
  onUpgradeClick?: () => void;
}

export function UpgradePrompt({ feature, variant = 'inline', onUpgradeClick }: UpgradePromptProps) {
  const { openCheckout, getTierPricing } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const featureInfo = FEATURE_INFO[feature];
  const recommendedTier = featureInfo.minimumTier;
  const recommendedPricing = getTierPricing(recommendedTier);

  async function handleUpgrade() {
    if (onUpgradeClick) {
      onUpgradeClick();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await openCheckout(recommendedTier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoading(false);
    }
  }

  if (variant === 'button') {
    return (
      <button
        onClick={handleUpgrade}
        disabled={loading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          fontSize: '13px',
          background: 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          fontWeight: '500',
        }}
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <>
            <Lock size={14} />
            Upgrade to Unlock
          </>
        )}
      </button>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '12px 16px',
          background:
            'linear-gradient(135deg, rgba(var(--purple-rgb), 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid rgba(var(--purple-rgb), 0.2)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, var(--purple-500) 0%, #EC4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Crown size={18} color="white" />
          </div>
          <div>
            <p
              style={{
                fontWeight: '600',
                color: 'var(--gray-900)',
                fontSize: '14px',
                marginBottom: '2px',
              }}
            >
              {featureInfo.name} requires {recommendedPricing?.name || 'Basic'}
            </p>
            <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{featureInfo.description}</p>
          </div>
        </div>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            background: 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontWeight: '500',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <>
              <Zap size={14} />
              Upgrade
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    );
  }

  // Default inline variant
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        background:
          'linear-gradient(135deg, rgba(var(--purple-rgb), 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
        borderRadius: 'var(--radius-lg)',
        border: '1px dashed rgba(var(--purple-rgb), 0.3)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background:
            'linear-gradient(135deg, rgba(var(--purple-rgb), 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '12px',
        }}
      >
        <Sparkles size={22} style={{ color: 'var(--purple-500)' }} />
      </div>
      <h4
        style={{
          fontWeight: '600',
          color: 'var(--gray-800)',
          marginBottom: '4px',
          fontSize: '14px',
        }}
      >
        {featureInfo.name}
      </h4>
      <p
        style={{
          fontSize: '12px',
          color: 'var(--gray-500)',
          marginBottom: '14px',
          maxWidth: '240px',
        }}
      >
        {featureInfo.description}
      </p>
      {error && (
        <p style={{ fontSize: '12px', color: 'var(--color-danger)', marginBottom: '10px' }}>
          {error}
        </p>
      )}
      <button
        onClick={handleUpgrade}
        disabled={loading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          fontSize: '13px',
          background: 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          fontWeight: '500',
        }}
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <>
            <Zap size={14} />
            Upgrade to {recommendedPricing?.name || 'Basic'}
          </>
        )}
      </button>
      <p style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '10px' }}>
        Starting at {recommendedPricing?.price_display || '$9.99'}/
        {recommendedPricing?.interval || 'month'}
      </p>
    </div>
  );
}

/**
 * PremiumBadge - Small badge to indicate premium features
 */
export function PremiumBadge({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const iconSize = size === 'sm' ? 10 : 12;
  const padding = size === 'sm' ? '2px 6px' : '3px 8px';
  const fontSize = size === 'sm' ? '10px' : '11px';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding,
        background: 'linear-gradient(135deg, var(--purple-500) 0%, #EC4899 100%)',
        borderRadius: '20px',
        fontSize,
        fontWeight: '600',
        color: 'white',
      }}
    >
      <Crown size={iconSize} />
      PRO
    </span>
  );
}

/**
 * AICreditsIndicator - Show remaining AI credits
 */
export function AICreditsIndicator() {
  const { aiCreditsRemaining, tier, openCheckout } = useSubscription();
  const [loading, setLoading] = useState(false);

  if (tier === 'FREE') {
    return null;
  }

  const isLow = aiCreditsRemaining < 10;
  const isEmpty = aiCreditsRemaining === 0;

  async function handleBuyMore() {
    setLoading(true);
    try {
      await openCheckout('ATHLETE_PRO');
    } catch {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        background: isEmpty
          ? 'rgba(239, 68, 68, 0.1)'
          : isLow
            ? 'rgba(245, 158, 11, 0.1)'
            : 'rgba(var(--purple-rgb), 0.1)',
        borderRadius: 'var(--radius-md)',
        fontSize: '12px',
      }}
    >
      <Sparkles
        size={14}
        style={{
          color: isEmpty ? 'var(--color-danger)' : isLow ? '#f59e0b' : 'var(--purple-500)',
        }}
      />
      <span style={{ color: 'var(--gray-700)', fontWeight: '500' }}>
        {aiCreditsRemaining} AI credits
      </span>
      {(isEmpty || isLow) && (
        <button
          onClick={handleBuyMore}
          disabled={loading}
          style={{
            padding: '2px 8px',
            fontSize: '11px',
            background: 'var(--purple-500)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {loading ? '...' : 'Get more'}
        </button>
      )}
    </div>
  );
}
